import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  PhoneCall,
  CheckCircle,
  X,
  Inbox,
  Headphones,
  ClipboardCheck,
  History,
  LogOut,
  Calendar,
  Search,
  AlertCircle,
  ShieldCheck,
  Clock,
  User,
} from 'lucide-react';

// ─── Tokens ────────────────────────────────────────────────────────────────
const tokens = {
  bg: '#f6f7fb',
  surface: '#ffffff',
  surfaceSoft: '#f1f5f9',
  surfaceTint: '#fafbff',
  ink: '#0f172a',
  body: '#334155',
  muted: '#64748b',
  subtle: '#94a3b8',
  hairline: '#e5e7eb',
  hairlineSoft: '#eef0f4',
  brand: '#6366f1',
  brandDeep: '#4f46e5',
  brandSoft: 'rgba(99, 102, 241, 0.08)',
  brand33: 'rgba(99, 102, 241, 0.32)',
  success: '#10b981',
  successSoft: 'rgba(16, 185, 129, 0.10)',
  successDeep: '#065f46',
  warning: '#f59e0b',
  warningSoft: 'rgba(245, 158, 11, 0.12)',
  warningDeep: '#92400e',
  danger: '#ef4444',
  dangerSoft: 'rgba(239, 68, 68, 0.10)',
  dangerDeep: '#991b1b',
  shadowSm: '0 1px 2px rgba(15, 23, 42, 0.04)',
  shadowMd: '0 4px 12px rgba(15, 23, 42, 0.06)',
  shadowLg: '0 12px 32px rgba(15, 23, 42, 0.10)',
  radiusSm: '8px',
  radiusMd: '12px',
  radiusLg: '16px',
  radiusXl: '20px',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Layout ────────────────────────────────────────────────────────────────
const Shell = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 260px 1fr;
  background: ${tokens.bg};
  color: ${tokens.body};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  border-right: 1px solid ${tokens.hairline};
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;

  @media (max-width: 968px) {
    display: none;
  }
`;

const SidebarBrand = styled.div`
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, ${tokens.brand} 0%, ${tokens.brandDeep} 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  .text {
    font-size: 1.05rem;
    font-weight: 700;
    color: ${tokens.ink};
    letter-spacing: -0.01em;
  }
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const NavItem = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  border-radius: ${tokens.radiusMd};
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  color: ${p => p.$active ? tokens.brand : tokens.body};
  background: ${p => p.$active ? tokens.brandSoft : 'transparent'};
  transition: background 0.15s, color 0.15s;

  svg {
    flex-shrink: 0;
    color: ${p => p.$active ? tokens.brand : tokens.subtle};
  }

  .badge {
    margin-left: auto;
    background: ${tokens.danger};
    color: white;
    border-radius: 999px;
    padding: 0.05rem 0.45rem;
    font-size: 0.72rem;
    font-weight: 700;
  }

  &:hover {
    background: ${p => p.$active ? tokens.brand33 : tokens.surfaceSoft};
    color: ${p => p.$active ? tokens.brandDeep : tokens.ink};
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${tokens.hairline};
`;

const UserCard = styled.div`
  padding: 0.85rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  .name { font-size: 0.85rem; font-weight: 700; color: ${tokens.ink}; }
  .role { font-size: 0.72rem; color: ${tokens.muted}; }
`;

const LogoutBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.85rem;
  border-radius: ${tokens.radiusMd};
  cursor: pointer;
  color: ${tokens.danger};
  font-weight: 600;
  font-size: 0.9rem;
  width: 100%;

  &:hover { background: ${tokens.dangerSoft}; }
`;

const Main = styled.div`
  padding: 1.75rem 2rem 4rem;
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: 1rem 1rem 3rem;
  }
`;

const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${tokens.ink};
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${tokens.brand}; }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.9rem;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 999px;
  min-width: 280px;
  box-shadow: ${tokens.shadowSm};

  input {
    border: 0;
    background: transparent;
    outline: none;
    font: inherit;
    flex: 1;
    color: ${tokens.ink};

    &::placeholder { color: ${tokens.subtle}; }
  }

  svg { color: ${tokens.subtle}; }
`;

// ─── Order rows ────────────────────────────────────────────────────────────
const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const Row = styled.article`
  position: relative;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusLg};
  padding: 0.95rem 1.05rem 0.95rem 1.4rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  transition: border-color 0.15s, box-shadow 0.15s;
  animation: ${fadeUp} 0.25s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${p => p.$stripe || tokens.subtle};
    border-radius: ${tokens.radiusLg} 0 0 ${tokens.radiusLg};
  }

  &:hover {
    border-color: ${tokens.brand33};
    box-shadow: ${tokens.shadowMd};
  }
`;

const RowMain = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RowTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const OrderHash = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${tokens.ink};
  font-variant-numeric: tabular-nums;

  &::before { content: '#'; color: ${tokens.subtle}; font-weight: 500; margin-right: 1px; }
`;

const CustomerName = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${tokens.body};
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${p => p.$bg || tokens.surfaceSoft};
  color: ${p => p.$fg || tokens.body};
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem 1.2rem;
  font-size: 0.82rem;
  color: ${tokens.muted};

  > span { display: inline-flex; align-items: center; gap: 0.35rem; }
  strong { font-weight: 600; color: ${tokens.body}; font-variant-numeric: tabular-nums; }
  svg { color: ${tokens.subtle}; }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const PhoneLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  background: ${tokens.successSoft};
  color: ${tokens.successDeep};
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid rgba(16, 185, 129, 0.25);

  &:hover { background: ${tokens.success}; color: white; }
`;

const PhoneEmpty = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  background: ${tokens.surfaceSoft};
  color: ${tokens.subtle};
  font-size: 0.82rem;
`;

const Btn = styled.button`
  appearance: none;
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background 0.15s, transform 0.1s;

  &.primary { background: ${tokens.brand}; color: white; &:hover { background: ${tokens.brandDeep}; } }
  &.success { background: ${tokens.success}; color: white; &:hover { background: #059669; } }
  &.secondary { background: ${tokens.surface}; color: ${tokens.body}; border-color: ${tokens.hairline}; &:hover { border-color: ${tokens.brand33}; color: ${tokens.ink}; } }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:active { transform: translateY(1px); }
`;

const Empty = styled.div`
  text-align: center;
  padding: 2.25rem 1.25rem;
  border: 1.5px dashed ${tokens.hairline};
  border-radius: ${tokens.radiusLg};
  background: ${tokens.surfaceTint};
  color: ${tokens.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  .icon-wrap {
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: ${tokens.brandSoft};
    color: ${tokens.brand};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  h4 { margin: 0.3rem 0 0; font-size: 0.95rem; color: ${tokens.ink}; font-weight: 600; }
  p  { margin: 0; font-size: 0.85rem; }
`;

// ─── Modal ─────────────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.15s ease-out;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: ${tokens.surface};
  padding: 1.85rem 1.85rem 1.5rem;
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 540px;
  border: 1px solid ${tokens.hairline};
  box-shadow: ${tokens.shadowLg};
  animation: ${fadeUp} 0.25s ease-out;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const ModalTitleEl = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${tokens.ink};
  display: flex;
  align-items: center;
  gap: 0.55rem;

  .icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${tokens.successSoft};
    color: ${tokens.success};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalCloseBtn = styled.button`
  appearance: none;
  background: ${tokens.surfaceSoft};
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.muted};

  &:hover { background: ${tokens.danger}; color: white; border-color: ${tokens.danger}; }
`;

const ModalText = styled.p`
  color: ${tokens.body};
  line-height: 1.55;
  margin: 0 0 1rem;
  font-size: 0.92rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  margin: 0.4rem 0 1rem;
  padding: 0.85rem 1rem;
  background: ${tokens.surfaceTint};
  border: 1px solid ${tokens.hairlineSoft};
  border-radius: ${tokens.radiusMd};
  font-size: 0.88rem;

  > div {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;

    .label { color: ${tokens.muted}; }
    .value { font-weight: 700; color: ${tokens.ink}; }
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.95rem;
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusMd};
  background: ${tokens.surfaceTint};
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 90px;
  box-sizing: border-box;
  margin-bottom: 1rem;

  &:focus { outline: none; border-color: ${tokens.brand}; background: ${tokens.surface}; box-shadow: 0 0 0 3px ${tokens.brandSoft}; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.7rem;
  > button { flex: 1; }
`;

const InlineError = styled.div`
  background: ${tokens.dangerSoft};
  border: 1px solid #fecaca;
  color: ${tokens.dangerDeep};
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  font-size: 0.82rem;
  margin-bottom: 0.7rem;
`;

const WaiverNote = styled.div`
  background: ${tokens.warningSoft};
  border: 1px solid ${tokens.warning};
  color: ${tokens.warningDeep};
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  font-size: 0.82rem;
  margin-bottom: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────
const fullName = (visitor) => {
  if (!visitor) return 'Unknown customer';
  const composed = `${visitor.firstName || ''} ${visitor.lastName || ''}`.trim();
  if (composed) return composed;
  if (visitor.gender) return `${visitor.gender} (${visitor.ageGroup || '—'})`;
  return 'Unknown customer';
};

const fmtMoney = (n) => `${(Number(n) || 0).toLocaleString()} DZD`;

const daysSince = (date) => {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

// ─── Component ─────────────────────────────────────────────────────────────
const CustomerSupportDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'recent'
  const [orders, setOrders] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const [surveyModal, setSurveyModal] = useState({ open: false, order: null });
  const [surveyNotes, setSurveyNotes] = useState('');
  const [surveySaving, setSurveySaving] = useState(false);
  const [surveyError, setSurveyError] = useState('');

  const fetchPending = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/orders/customer-support/pending-survey', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Pending survey fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Reuse the generic /api/orders endpoint, filtered to completed
      // and limited to a reasonable window. Customer support sees all
      // stores so we don't add a store filter on the client.
      const { data } = await axios.get('/api/orders?status=completed&limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecentOrders((data.orders || []).filter(o => o.surveyCompletedAt));
    } catch (err) {
      console.error('Recent surveys fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    fetchRecent();
  }, [fetchPending, fetchRecent, refreshTick]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter(o =>
      o.orderNumber?.toLowerCase().includes(term) ||
      `${o.visitor?.firstName || ''} ${o.visitor?.lastName || ''}`.toLowerCase().includes(term) ||
      o.visitor?.phone?.toLowerCase().includes(term) ||
      o.store?.name?.toLowerCase().includes(term)
    );
  }, [orders, search]);

  const filteredRecent = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return recentOrders;
    return recentOrders.filter(o =>
      o.orderNumber?.toLowerCase().includes(term) ||
      `${o.visitor?.firstName || ''} ${o.visitor?.lastName || ''}`.toLowerCase().includes(term)
    );
  }, [recentOrders, search]);

  const openSurvey = (order) => {
    setSurveyError('');
    setSurveyNotes('');
    setSurveyModal({ open: true, order });
  };

  const closeSurvey = () => {
    setSurveyModal({ open: false, order: null });
    setSurveyNotes('');
    setSurveyError('');
  };

  const submitSurvey = async () => {
    const order = surveyModal.order;
    if (!order) return;
    setSurveySaving(true);
    setSurveyError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/orders/${order._id}/survey-done`, {
        notes: surveyNotes || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeSurvey();
      setRefreshTick(t => t + 1);
    } catch (err) {
      setSurveyError(err.response?.data?.message || 'Failed to close out the survey.');
    } finally {
      setSurveySaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const visibleList = activeTab === 'pending' ? filtered : filteredRecent;

  return (
    <Shell>
      <Sidebar>
        <SidebarBrand>
          <div className="icon"><Headphones size={20} /></div>
          <div className="text">{t('support.customerSupport')}</div>
        </SidebarBrand>

        <SidebarNav>
          <NavItem $active={activeTab === 'pending'} onClick={() => setActiveTab('pending')}>
            <ClipboardCheck size={18} />
            {t('support.pendingSurveys')}
            {orders.length > 0 && <span className="badge">{orders.length}</span>}
          </NavItem>
          <NavItem $active={activeTab === 'recent'} onClick={() => setActiveTab('recent')}>
            <History size={18} />
            {t('support.recent')}
          </NavItem>
        </SidebarNav>

        <SidebarFooter>
          <UserCard>
            <div className="name">{user.firstName} {user.lastName}</div>
            <div className="role">{t('support.customerSupport')}</div>
          </UserCard>
          <LogoutBtn onClick={handleLogout}>
            <LogOut size={18} />
            {t('support.logout')}
          </LogoutBtn>
        </SidebarFooter>
      </Sidebar>

      <Main>
        <PageHeader>
          <PageTitle>
            {activeTab === 'pending' ? <ClipboardCheck size={22} /> : <History size={22} />}
            {activeTab === 'pending' ? t('support.pendingAfterSaleSurveys') : t('support.recentlyCompletedSurveys')}
          </PageTitle>
          <SearchBar>
            <Search size={16} />
            <input
              type="text"
              placeholder={t('support.searchByOrderCustomerName')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBar>
        </PageHeader>

        <div style={{ marginBottom: '1rem', color: tokens.muted, fontSize: '0.88rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={14} />
          {todayLabel}
        </div>

        {activeTab === 'pending' ? (
          <RowList>
            {loading ? (
              <Empty>
                <div className="icon-wrap"><Inbox size={22} /></div>
                <h4>{t('support.loading')}</h4>
                <p>{t('support.fetchingDeliveredOrdersAwaitingYour')}</p>
              </Empty>
            ) : visibleList.length === 0 ? (
              <Empty>
                <div className="icon-wrap"><CheckCircle size={22} /></div>
                <h4>{search ? t('support.noMatches') : t('support.allCaughtUp')}</h4>
                <p>{search ? t('support.tryADifferentSearch') : t('support.noCustomersWaitingOnA')}</p>
              </Empty>
            ) : (
              visibleList.map(order => {
                const phone = order.visitor?.phone;
                const ageDays = daysSince(order.deliveredAt);
                const total = Number(order.total) || 0;
                const paid = Number(order.payment?.amount) || 0;
                const balance = Math.max(0, total - paid);
                const hasWaiver = order.deliveryWaiverSigned && balance > 0;

                return (
                  <Row key={order._id} $stripe={tokens.success}>
                    <RowMain>
                      <RowTopLine>
                        <OrderHash>{order.orderNumber}</OrderHash>
                        <CustomerName>{fullName(order.visitor)}</CustomerName>
                        <Pill $bg={tokens.successSoft} $fg={tokens.successDeep}>{t('support.delivered')}</Pill>
                        {order.store?.name && (
                          <Pill $bg={tokens.brandSoft} $fg={tokens.brandDeep}>
                            {order.store.name}
                          </Pill>
                        )}
                        {hasWaiver && (
                          <Pill $bg={tokens.warningSoft} $fg={tokens.warningDeep}>
                            <ShieldCheck size={11} /> {t('support.waiverOwes')} {fmtMoney(balance)}
                          </Pill>
                        )}
                      </RowTopLine>
                      <RowMeta>
                        <span><Clock size={12} />{t('support.delivered')} {ageDays === 0 ? 'today' : ageDays === 1 ? 'yesterday' : `${ageDays}d ago`}</span>
                        {order.deliveredBy && (
                          <span><User size={12} />{t('support.by')} {order.deliveredBy.firstName} {order.deliveredBy.lastName}</span>
                        )}
                        <span>{t('support.total')} <strong>{fmtMoney(total)}</strong></span>
                      </RowMeta>
                    </RowMain>
                    <RowActions>
                      {phone ? (
                        <PhoneLink href={`tel:${phone}`} title={`Call ${phone}`}>
                          <PhoneCall size={14} /> {phone}
                        </PhoneLink>
                      ) : (
                        <PhoneEmpty><Phone size={14} /> {t('support.noPhone')}</PhoneEmpty>
                      )}
                      <Btn className="success" onClick={() => openSurvey(order)}>
                        <CheckCircle size={14} /> {t('support.markSurveyDone')}
                      </Btn>
                    </RowActions>
                  </Row>
                );
              })
            )}
          </RowList>
        ) : (
          <RowList>
            {visibleList.length === 0 ? (
              <Empty>
                <div className="icon-wrap"><History size={22} /></div>
                <h4>{t('support.noCompletedSurveysYet')}</h4>
                <p>{t('support.closedOrdersWillAppearHere')}</p>
              </Empty>
            ) : (
              visibleList.map(order => (
                <Row key={order._id} $stripe={tokens.subtle}>
                  <RowMain>
                    <RowTopLine>
                      <OrderHash>{order.orderNumber}</OrderHash>
                      <CustomerName>{fullName(order.visitor)}</CustomerName>
                      <Pill $bg={tokens.surfaceSoft} $fg={tokens.body}>{t('support.completed')}</Pill>
                    </RowTopLine>
                    <RowMeta>
                      {order.surveyCompletedAt && (
                        <span><CheckCircle size={12} />{t('support.closed')} {new Date(order.surveyCompletedAt).toLocaleDateString()}</span>
                      )}
                      {order.surveyNotes && <span>“{order.surveyNotes}”</span>}
                    </RowMeta>
                  </RowMain>
                </Row>
              ))
            )}
          </RowList>
        )}

        {/* ── Survey modal ─────────────────────────────────────────── */}
        {surveyModal.open && surveyModal.order && (() => {
          const order = surveyModal.order;
          const total = Number(order.total) || 0;
          const paid = Number(order.payment?.amount) || 0;
          const balance = Math.max(0, total - paid);
          return (
            <ModalOverlay onClick={closeSurvey}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHead>
                  <ModalTitleEl>
                    <span className="icon-wrap"><ClipboardCheck size={20} /></span>
                    {t('support.markSurveyDone')}
                  </ModalTitleEl>
                  <ModalCloseBtn onClick={closeSurvey}><X size={18} /></ModalCloseBtn>
                </ModalHead>

                <ModalText>
                  {t('support.closingOut')} <strong>#{order.orderNumber}</strong> for {fullName(order.visitor)}.
                </ModalText>

                <SummaryGrid>
                  <div><span className="label">{t('support.store')}</span><span className="value">{order.store?.name || '—'}</span></div>
                  <div><span className="label">{t('support.delivered')}</span><span className="value">{order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString() : '—'}</span></div>
                  <div><span className="label">{t('support.total')}</span><span className="value">{fmtMoney(total)}</span></div>
                  <div><span className="label">{t('support.balance')}</span><span className="value" style={{ color: balance > 0 ? tokens.dangerDeep : tokens.successDeep }}>{fmtMoney(balance)}</span></div>
                </SummaryGrid>

                {order.deliveryWaiverSigned && balance > 0 && (
                  <WaiverNote>
                    <ShieldCheck size={14} />
                    {t('support.customerSignedTheWaiverAt')}
                  </WaiverNote>
                )}

                {surveyError && <InlineError><AlertCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{surveyError}</InlineError>}

                <FormTextarea
                  placeholder={t('support.surveyNotesFeedbackFromThe')}
                  value={surveyNotes}
                  onChange={(e) => setSurveyNotes(e.target.value)}
                />

                <ModalActions>
                  <Btn className="secondary" onClick={closeSurvey} disabled={surveySaving}>{t('support.cancel')}</Btn>
                  <Btn className="success" onClick={submitSurvey} disabled={surveySaving}>
                    {surveySaving ? t('support.closing') : t('support.confirmCloseOrder')}
                  </Btn>
                </ModalActions>
              </ModalContent>
            </ModalOverlay>
          );
        })()}
      </Main>
    </Shell>
  );
};

export default CustomerSupportDashboard;
